import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import Layout from "@/components/Layout";
import { Link } from "react-router-dom";
import { useState } from "react";
import { Syringe, Heart, Zap, Droplets, Microscope, Stethoscope, Sparkles, Shield, Activity, Leaf, Scissors, Plus } from "lucide-react";
const Treatments = () => {
  const [activeFilter, setActiveFilter] = useState("All");
  const treatments = [{
    name: "Botox",
    description: "Smooth fine lines and wrinkles with precision injections. FDA-approved neuromodulator for facial rejuvenation.",
    price: "Starting at $12/unit",
    category: "Injectables",
    icon: Syringe,
    href: "/treatments/botox",
    details: "Results last 3-4 months. Minimal downtime. Best for frown lines, crow's feet, and forehead wrinkles."
  }, {
    name: "Lip Filler",
    description: "Add volume and definition to your lips with hyaluronic acid fillers for natural-looking enhancement.",
    price: "Starting at $650",
    category: "Injectables",
    icon: Heart,
    href: "/treatments/lip-filler",
    details: "Immediate results. Lasts 6-12 months. Customizable volume and shape enhancement."
  }, {
    name: "Kybella",
    description: "Non-surgical double chin reduction using deoxycholic acid to eliminate submental fat permanently.",
    price: "Starting at $800",
    category: "Injectables",
    icon: Shield,
    href: "/treatments/kybella",
    details: "Permanent results. 2-4 treatments needed. FDA-approved fat reduction."
  }, {
    name: "AquaFirme Facial",
    description: "Deep hydration with vitamin infusion. Multi-step treatment for instant glow and skin rejuvenation.",
    price: "Starting at $199",
    category: "Skin",
    icon: Droplets,
    href: "/treatments/aquafirme",
    details: "No downtime. Immediate results. Perfect for all skin types and ages."
  }, {
    name: "VI Peel",
    description: "Chemical peel for improved skin texture, tone, and clarity with minimal downtime and maximum results.",
    price: "Starting at $300",
    category: "Skin",
    icon: Sparkles,
    href: "/treatments/vi-peel",
    details: "7-day peeling process. Improves acne, pigmentation, and fine lines."
  }, {
    name: "Laser Facial",
    description: "Advanced laser technology for skin rejuvenation, targeting sun damage, fine lines, and uneven texture.",
    price: "Starting at $350",
    category: "Skin",
    icon: Zap,
    href: "/treatments/laser-facial",
    details: "Minimal downtime. Stimulates collagen production. Improves skin tone and texture."
  }, {
    name: "RF Microneedling",
    description: "Collagen induction with radiofrequency energy for skin tightening and texture improvement.",
    price: "Starting at $649",
    category: "Skin",
    icon: Microscope,
    href: "/treatments/rf-microneedling",
    details: "3-5 days downtime. Results improve over 3-6 months. Best for acne scars and texture."
  }, {
    name: "PRF",
    description: "Platelet-rich fibrin therapy for natural rejuvenation using your body's own healing factors.",
    price: "Starting at $449",
    category: "Skin",
    icon: Activity,
    href: "/treatments/prf",
    details: "Natural healing. Gradual improvement over 2-3 months. Excellent for under-eye rejuvenation."
  }, {
    name: "Skin Boosters",
    description: "Deep hydration and skin quality improvement with micro-injections of hyaluronic acid.",
    price: "Starting at $550",
    category: "Skin",
    icon: Plus,
    href: "/treatments/skin-boosters",
    details: "Improves skin hydration, elasticity, and radiance. Results last 6-9 months."
  }, {
    name: "IV Therapy",
    description: "Vitamin and hydration boost from within. Customized nutrient infusions for wellness and energy.",
    price: "Starting at $150",
    category: "Wellness",
    icon: Stethoscope,
    href: "/treatments/iv-therapy",
    details: "Immediate hydration. Custom vitamin blends. Boosts energy and immunity."
  }, {
    name: "Dermaplaning",
    description: "Gentle exfoliation for smoother skin by removing dead skin cells and fine facial hair.",
    price: "Starting at $125",
    category: "Skin",
    icon: Scissors,
    href: "/treatments/dermaplaning",
    details: "No downtime. Immediate smooth skin. Perfect add-on to other treatments."
  }, {
    name: "Lipotropic Injections",
    description: "Metabolism support and energy boost with targeted fat-burning vitamin injections.",
    price: "Starting at $75",
    category: "Wellness",
    icon: Leaf,
    href: "/treatments/lipotropic-injections",
    details: "Weekly injections. Supports weight management. Boosts energy and metabolism."
  }];
  const filters = ["All", "Injectables", "Skin", "Wellness"];
  const filteredTreatments = activeFilter === "All" ? treatments : treatments.filter(treatment => treatment.category === activeFilter);
  return <Layout>
      {/* Hero Section */}
      <section className="py-20 bg-juv-gradient-secondary">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h1 className="text-4xl md:text-6xl font-bold text-juv-deep-chestnut mb-6 font-juv-heading">
              Explore Our Signature Treatments
            </h1>
            <p className="text-xl text-juv-deep-chestnut max-w-3xl mx-auto font-juv-body leading-relaxed">
              From injectables to wellness boosters, every treatment at JUV is crafted to help you feel confident in your skin — without pressure or pretense.
            </p>
          </div>

          {/* Filter Tabs */}
          <div className="flex flex-wrap justify-center gap-4 mb-12">
            {filters.map(filter => <Button key={filter} onClick={() => setActiveFilter(filter)} className={`px-6 py-3 rounded-full transition-all duration-300 ${activeFilter === filter ? "bg-juv-terracotta hover:bg-juv-deep-chestnut text-white" : "bg-white text-juv-deep-chestnut border border-juv-rose-clay hover:bg-juv-soft-blush"}`}>
                {filter}
              </Button>)}
          </div>
        </div>
      </section>
      
      {/* Treatments Grid */}
      <section className="py-16 bg-juv-cream-white">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredTreatments.map((treatment, index) => <Card key={index} className="group hover:scale-105 transition-all duration-300 shadow-luxury hover:shadow-editorial border-juv-rose-clay/20 bg-slate-50">
                <CardContent className="p-8 bg-slate-50">
                  {/* Treatment Icon */}
                  <div className="text-center mb-6">
                    <div className="w-20 h-20 bg-juv-gradient-secondary rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                      <treatment.icon className="w-10 h-10 text-juv-terracotta" />
                    </div>
                    <span className="inline-block px-3 py-1 bg-juv-soft-blush text-juv-deep-chestnut rounded-full text-xs font-semibold border border-juv-rose-clay">
                      {treatment.category}
                    </span>
                  </div>
                  
                  <h3 className="text-xl font-bold text-juv-deep-chestnut mb-3 font-juv-heading text-center">
                    {treatment.name}
                  </h3>
                  
                  <p className="text-juv-deep-chestnut mb-4 font-juv-body leading-relaxed text-center">
                    {treatment.description}
                  </p>
                  
                  <p className="text-sm text-juv-deep-chestnut/60 mb-4 text-center leading-relaxed">
                    {treatment.details}
                  </p>
                  
                  <p className="text-lg font-semibold text-juv-terracotta mb-6 text-center">
                    {treatment.price}
                  </p>
                  
                  <Button asChild className="w-full bg-juv-terracotta hover:bg-juv-deep-chestnut text-white rounded-full">
                    <Link to={treatment.href}>Learn More →</Link>
                  </Button>
                </CardContent>
              </Card>)}
          </div>

          {/* Glow Quiz CTA */}
          <div className="text-center mt-16 bg-gradient-to-br from-juv-soft-blush to-juv-rose-clay/30 rounded-2xl p-12">
            <div className="mb-4">
              <Sparkles className="w-12 h-12 text-juv-terracotta mx-auto" />
            </div>
            <h2 className="text-3xl font-bold text-juv-deep-chestnut mb-6 font-juv-heading">
              Not Sure What to Book?
            </h2>
            <p className="text-xl text-juv-deep-chestnut mb-8 font-juv-body">
              Take our Glow Quiz to discover the perfect treatment for your goals.
            </p>
            <Button asChild size="lg" className="bg-juv-terracotta hover:bg-juv-deep-chestnut text-white px-8 py-4 rounded-full">
              <Link to="/quiz">Take the Glow Quiz</Link>
            </Button>
          </div>
        </div>
      </section>
    </Layout>;
};
export default Treatments;